/**
 * frontend/src/components/PartnerData.js  (Phase 12)
 *
 * Read-only strip showing what a PSP reported through the Partner API for a
 * line item: model review state, estimated cost, batch reference, inbound
 * tracking, packing-slip record. Used in the PSP portal's LineItemPanel and
 * the admin Operations panel. When `onReview` is passed (admin only), a
 * pending/approved/rejected submission gets Approve / Reject controls.
 *
 * Renders nothing when there's nothing to show, so portal-only items look
 * exactly as they did before Phase 12.
 */
import React, { useState } from 'react';

// Phase 12b — model review moved to PSPModels; this strip shows what the PSP
// reported about production and billing.
const PAYMENT = {
  not_invoiced:     { label: 'Not invoiced',     bg: '#F3F4F6', color: '#374151' },
  awaiting_payment: { label: 'Awaiting payment', bg: '#FEF3C7', color: '#92400E' },
  paid:             { label: 'Paid',             bg: '#D1FAE5', color: '#065F46' },
};

const fmtDT = (d) => (d ? new Date(d).toLocaleString() : '');
const money = (n) => (n == null || n === '' ? null : `$${Number(n).toFixed(2)}`);
const vol = (n) => (n == null || n === '' ? null : `${Number(n).toFixed(2)} cm³`);

export function hasPartnerData(d) {
  return !!(d && (d.estimatedCost != null || d.pspBatchId || d.invoiceRef || d.paymentStatus || d.netVolume != null || d.shipmentGroupId
    || d.rawVolumeEstimate != null || d.inboundShipments?.length || d.assignedPspCompany));
}

export default function PartnerData({ data, showAssignee = false }) {
  const [showSlip, setShowSlip] = useState(false);
  if (!hasPartnerData(data)) return null;
  const pay = data.paymentStatus ? PAYMENT[data.paymentStatus] : null;
  const label = { fontSize: 11, color: '#6b7280' };
  const val   = { fontSize: 12, color: '#111827', fontWeight: 500 };

  return (
    <div style={{ marginTop: 12, border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        PSP reported
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', alignItems: 'flex-start' }}>
        {showAssignee && (
          <div><div style={label}>Assigned PSP</div><div style={val}>{data.assignedPspCompany?.name || 'Unassigned'}</div></div>
        )}
        {pay && (
          <div><div style={label}>Payment</div>
            <span style={{ display: 'inline-block', marginTop: 2, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: pay.bg, color: pay.color }}>{pay.label}</span>
          </div>
        )}
        {data.invoiceRef && <div><div style={label}>Invoice</div><div style={val}>{data.invoiceRef}</div></div>}
        {data.shipmentGroupId && <div><div style={label}>Shipment group</div><div style={val}>{data.shipmentGroupId}</div></div>}
        {vol(data.netVolume) && <div><div style={label}>Net volume (billed)</div><div style={val}>{vol(data.netVolume)}</div></div>}
        {vol(data.rawVolumeEstimate) && <div><div style={label}>RAW volume est.</div><div style={val}>{vol(data.rawVolumeEstimate)}</div></div>}
        {money(data.estimatedCost) && <div><div style={label}>Est. printing cost</div><div style={val}>{money(data.estimatedCost)}</div></div>}
        {data.pspBatchId && (
          <div>
            <div style={label}>PSP batch</div>
            <div style={val}>
              {data.pspBatchId}
              {data.packingSlip?.slipData && (
                <button onClick={() => setShowSlip(s => !s)}
                  style={{ marginLeft: 8, fontSize: 11, border: 'none', background: 'none', color: 'var(--brand, #2563eb)', cursor: 'pointer', padding: 0 }}>
                  {showSlip ? 'Hide slip data' : 'View slip data'}
                </button>
              )}
            </div>
          </div>
        )}
        {data.inboundShipments?.length > 0 && (
          <div>
            <div style={label}>Inbound tracking (PSP → Solidiphy)</div>
            {data.inboundShipments.map((s, i) => (
              <div key={i} style={{ fontSize: 12, color: '#111827' }}>
                <span style={{ fontWeight: 500 }}>{s.carrier ? `${s.carrier} ` : ''}{s.trackingNumber}</span>
                {s.shipmentGroupId && <span style={{ color: '#6b7280' }}> · group {s.shipmentGroupId}</span>}
                <span style={{ color: '#9ca3af' }}> · {fmtDT(s.createdAt)}{s.pspName ? ` · ${s.pspName}` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {showSlip && data.packingSlip?.slipData && (
        <pre style={{ marginTop: 8, maxHeight: 220, overflow: 'auto', fontSize: 11, background: '#f9fafb', border: '0.5px solid #e5e7eb', borderRadius: 6, padding: 8 }}>
          {JSON.stringify(data.packingSlip.slipData, null, 2)}
        </pre>
      )}
    </div>
  );
}
